import * as React from 'react'
import {Component, Fragment} from 'react'
import {findDOMNode} from 'react-dom'
import {toJS} from 'mobx'
import {flatten, intersection, each} from 'lodash'
import {markFieldAsDirty} from '../../lib/helpers'
import {IFormBase} from '../../lib/forms/IFormBase'

///////////////////////////////////////////////////////////////////////////////

export type DirtyWrapperProps = {
  children?: any
  form: IFormBase<any>
  fields: string | string[]
  inputTimeout?: number
  blurTimeout?: number
  skipValidationOnBlur?: boolean
}

type FormElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

///////////////////////////////////////////////////////////////////////////////

export class DirtyWrapper extends Component<DirtyWrapperProps, any> {
  private el: FormElement
  private fields: string[]
  private initialValue: string | boolean
  private previousValue: string | boolean
  private dirty: boolean = false

  public static defaultProps: Partial<DirtyWrapperProps> = {
    inputTimeout: 0,
    blurTimeout: 0,
    skipValidationOnBlur: false,
  }

  constructor(props) {
    super(props)

    this.onInputText = this.onInputText.bind(this)
    this.onInputImmediate = this.onInputImmediate.bind(this)
    this.onBlur = this.onBlur.bind(this)

    this.getValue = this.getValue.bind(this)
    this.isValueChanged = this.isValueChanged.bind(this)
    this.markAsDirty = this.markAsDirty.bind(this)
  }

  componentDidMount() {
    let {fields} = this.props
    this.el = findDOMNode(this) as FormElement
    this.fields = flatten([fields])
    this.initialValue = this.getValue()
    this.previousValue = this.initialValue

    let {el, onInputImmediate, onInputText, onBlur} = this

    if (el) {
      if (el.tagName === 'INPUT') {
        if (el.type === 'checkbox' || el.type === 'radio') {
          el.addEventListener('change', onInputImmediate)
        } else {
          el.addEventListener('input', onInputText)
          el.addEventListener('blur', onBlur)
        }
      }

      if (el.tagName === 'SELECT') {
        el.addEventListener('input', onInputImmediate)
      }

      if (el.tagName === 'TEXTAREA') {
        el.addEventListener('input', onInputText)
        el.addEventListener('blur', onBlur)
      }
    }
  }

  componentWillUnmount() {
    let {el, onInputImmediate, onInputText, onBlur} = this

    if (el) {
      el.removeEventListener('input', onInputText)
      el.removeEventListener('input', onInputImmediate)
      el.removeEventListener('blur', onBlur)
    }
  }

  getValue(): string | boolean {
    let {el} = this

    if (el) {
      if (
        el.tagName === 'INPUT' &&
        (el.type === 'checkbox' || el.type === 'radio')
      ) {
        el = el as HTMLInputElement
        return el.checked
      } else {
        return el.value
      }
    }
  }

  onInputImmediate(e) {
    let {form, inputTimeout} = this.props

    setTimeout(() => {
      this.dirty = true
      this.markAsDirty()
      form.validate(true)
    }, inputTimeout)
  }

  onInputText(e) {
    let {form, inputTimeout} = this.props
    let {initialValue, dirty, getValue, isValueChanged, fields} = this

    setTimeout(() => {
      if (!dirty) {
        this.dirty = getValue() !== initialValue
      }

      if (dirty && isValueChanged()) {
        if (intersection(fields, toJS(form.dirtyFields)).length > 0) {
          form.validate(true)
        }
      }

      this.previousValue = getValue()
    }, inputTimeout)
  }

  onBlur(e) {
    let {form, blurTimeout, skipValidationOnBlur} = this.props
    let {dirty, markAsDirty} = this

    if (skipValidationOnBlur) {
      return
    }

    setTimeout(() => {
      if (dirty) {
        markAsDirty()
      }

      form.validate(true)
    }, blurTimeout)
  }

  // This is needed to get around bug in ie11 with excessive input event
  // See:
  // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/101220/
  // https://github.com/lovelope/vue/commit/15e8fa59b99890268b9a06ff263a6db131e0aa63
  isValueChanged() {
    return this.getValue() !== this.previousValue
  }

  markAsDirty() {
    let {form} = this.props

    each(this.fields, (field) => {
      markFieldAsDirty(form.dirtyFields, field)
    })
  }

  render() {
    return <Fragment>{this.props.children}</Fragment>
  }
}
